import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ElockReasonModel } from 'sections/LKSection/modules/ELockModule/stores/Reason/ElockReasonModel'
import { ElockReason } from 'sections/LKSection/modules/ELockModule/types'
import { getOrderItemPageKey } from 'sections/LKSection/modules/OrderModule/utils'
import { Margin } from 'components/ui'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Flex, Select, TextArea, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { PHOTO_VALIDATE } from 'shared/constants/values'

import { eLockReasonOptions, OrderKeys } from '../../../OrderModule/data'

export const MIN_DESCRIPTION_LENGTH = 10

type OrderELockFormProps = {
  uiStore: ElockReasonModel
  hideTitle?: boolean
  disabled?: boolean
}

export const OrderELockForm = observer(({ uiStore, disabled = false, hideTitle = false }: OrderELockFormProps) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    form.setFieldValue('reason', uiStore.eLockReasonValue)
    form.setFieldValue('description', uiStore.description)
  }, [uiStore.eLockReasonValue, uiStore.description])

  const deleteFileHandler = (index: number) => {
    const newFiles = [...uiStore.activateWithoutELockPhotos]
    newFiles.splice(index, 1)
    uiStore.setActivateWithoutELockPhotos(newFiles)
  }

  const getKey = (key: string) => `${OrderKeys.eLockModule}.${key}`

  return (
    <Form form={form} layout="vertical" requiredMark={false}>
      {!hideTitle && (
        <Margin mb={16}>
          <Title level={5}>{t(getOrderItemPageKey('Активация ПВЗ без замка'))}</Title>
        </Margin>
      )}

      <Form.Item label={t(getSharedKey('reason'))} name="reason">
        <Select
          value={uiStore.eLockReasonValue}
          onChange={(value: ElockReason) => uiStore.setELockReasonValue(value)}
          options={eLockReasonOptions.map((reason) => ({ value: reason.value, label: t(getKey(reason.label)) }))}
          disabled={disabled}
          placeholder={t(getKey('Выберите причину'))}
        />
      </Form.Item>

      <Form.Item<{ description: string }>
        label={t(getKey('Описание проблемы'))}
        name="description"
        rules={[
          { min: MIN_DESCRIPTION_LENGTH, message: t(getSharedKey('minLength'), { count: MIN_DESCRIPTION_LENGTH }) },
          { required: true, message: t(getSharedKey('required')) },
        ]}
      >
        <TextArea
          value={uiStore.description}
          onChange={(e) => uiStore.setDescription(e.target.value)}
          maxLength={200}
          rows={5}
          style={{ resize: 'none' }}
          disabled={disabled}
          placeholder={t(getKey('Опишите проблему'))}
        />
      </Form.Item>

      {!disabled ? (
        <Form.Item label={t(getKey('Прикрепить фото'))} tooltip={t(getKey('Фотографии с проблемами'))}>
          <Flex gap={8} wrap>
            {Array.from({ length: Math.min(3, uiStore.activateWithoutELockPhotos.length + 1) }).map((_, index) => (
              <UploadFile
                key={index}
                contentUrl={uiStore.activateWithoutELockPhotos[index]?.content ?? ''}
                onChange={(file) =>
                  uiStore.setActivateWithoutELockPhotos([...uiStore.activateWithoutELockPhotos, file])
                }
                onDelete={() => deleteFileHandler(index)}
                fileValidationOptions={PHOTO_VALIDATE}
                disabled={disabled}
              />
            ))}
          </Flex>
        </Form.Item>
      ) : (
        uiStore.activateWithoutELockPhotos.length > 0 && (
          <Form.Item label={t(getSharedKey('photos'))}>
            <Flex gap={8} wrap>
              {uiStore.activateWithoutELockPhotos.map((photo, index) => (
                <UploadFile key={index} contentUrl={photo.content} disabled={true} />
              ))}
            </Flex>
          </Form.Item>
        )
      )}
    </Form>
  )
})
